import React from "react";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import { useWebSocketContext } from "../../../context/WebSocketContext.tsx";
import { useDevicesContext } from "../../../context/DevicesContext.tsx";
import { useUserContext } from "../../../context/UserContext.tsx";
import { DataTable } from "../../../components/DataTable/index.ts";
import type { Column } from "../../../components/DataTable/index.ts";
import moment from "moment";

const BorderLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
  borderRadius: "50px",
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: "rgb(var(--surface-3))",
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: "50px",
    background:
      value > 20
        ? "linear-gradient(256deg, #649c35 0, #0aca91 100%)"
        : "linear-gradient(270deg, rgba(255, 57, 30, 0.82) 0.01%, #FFA1A1 99.98%)",
  },
}));

export default function IotBattery({ header }: { header?: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const { socketData, isConnected, updateVersion } = useWebSocketContext();
  const { globalstorevalue } = useUserContext();
  const { data } = useDevicesContext();

  React.useEffect(() => {
    if (data && data?.length > 0) {
      setLoading(false);
    }
  }, [data, globalstorevalue]);

  const mergedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const filteredData =
      globalstorevalue === null || globalstorevalue === ""
        ? data
        : data?.filter((store: any) => store?._id === globalstorevalue);

    const result = filteredData?.map((backendItem: any) => {
      const backendStoreId = backendItem?._id;
      const socketItem = socketData?.find((socket: any) => {
        const socketStoreId =
          typeof socket.store_id === "object" ? socket?.store_id?._id : socket?.store_id;
        return socketStoreId === backendStoreId;
      });

      const livePushButton = socketItem?.push_button;

      return livePushButton && isConnected
        ? {
            ...backendItem,
            configs: {
              ...backendItem.configs,
              sensors: {
                ...(backendItem.configs?.sensors || {}),
                push_button: {
                  ...(backendItem.configs?.sensors?.push_button || {}),
                  ...livePushButton,
                },
              },
            },
          }
        : backendItem;
    });

    return result?.filter((item: any) => item?.configs?.sensors?.push_button) || [];
  }, [data, socketData, isConnected, updateVersion, globalstorevalue]);

  const columns: Column[] = [
    {
      key: "name",
      label: "Store Name",
      width: "22%",
      render: (_: any, row: any) => (
        <span className="font-semibold text-[rgb(var(--text-primary))]">
          {row?.store_id?.name || row?.name || "Unknown Store"}
        </span>
      ),
    },
    {
      key: "last_updated",
      label: "Last Updated",
      width: "24%",
      render: (_: any, row: any) => {
        const pushButton = row?.configs?.sensors?.push_button;
        const lastUpdated = pushButton?.last_updated || row?.last_updated;
        return (
          <span className="text-[12px] text-[rgb(var(--text-secondary))]">
            {lastUpdated ? moment(lastUpdated).format("DD/MM/YYYY hh:mm:ss A") : "N/A"}
          </span>
        );
      },
    },
    {
      key: "battery_bar",
      label: "Battery Life Progress Bar",
      width: "34%",
      render: (_: any, row: any) => {
        const pushButton = row?.configs?.sensors?.push_button;
        const batteryValue = pushButton?.battery ?? row?.remaining_life ?? 0;
        return (
          <Stack spacing={2} sx={{ flexGrow: 1 }}>
            <BorderLinearProgress variant="determinate" value={batteryValue} sx={{ height: "8px" }} />
          </Stack>
        );
      },
    },
    {
      key: "battery_percent",
      label: "Battery Life(%)",
      width: "20%",
      align: "center",
      render: (_: any, row: any) => {
        const pushButton = row?.configs?.sensors?.push_button;
        const batteryValue = pushButton?.battery ?? row?.remaining_life ?? 0;
        const isLow = batteryValue <= 20;
        return (
          <span
            className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${
              isLow
                ? "text-[rgb(var(--danger))] bg-[rgba(184,56,56,0.1)]"
                : "text-[rgb(var(--success))] bg-[rgba(45,138,95,0.1)]"
            }`}
          >
            {batteryValue}%
          </span>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={mergedData}
      rowKey="_id"
      loading={loading}
      skeletonRows={3}
      emptyMessage="No push button data available."
      maxHeight="calc(100vh - 400px)"
      header={header}
    />
  );
}
